/*
 * One session: its laps, its setup, and the video lined up against both.
 *
 * The lap list is the centre of this screen. Each row carries a seek position,
 * so clicking a lap jumps the video to it - the camera is started by hand a few
 * seconds before the session, and knowing that lead is what makes "show me the
 * 43.859" a click rather than a hunt through nine minutes of footage.
 */

import { useState, useRef, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Camera, Copy, Play, SlidersHorizontal } from 'lucide-react'
import { api } from '../lib/api'
import type { SessionDetail as Detail } from '../lib/api'
import { clock, dateTime, lapTime, layoutName } from '../lib/format'
import { Empty, SetupBadge, Spinner } from '../components/bits'

export function SessionDetail() {
  const { id } = useParams()
  const [tab, setTab] = useState<'laps' | 'setup' | 'media'>('laps')

  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['session', id],
    queryFn: () => api.get<Detail>(`/api/sessions/${id}`),
  })

  // Video playback. The URL is a short-lived presigned link, so the browser
  // streams straight from object storage and seeking works over HTTP ranges.
  const videoRef = useRef<HTMLVideoElement>(null)
  const [pendingSeek, setPendingSeek] = useState<number | null>(null)
  const completeVideo = q.data?.videos.find((v) => v.complete)
  const videoUrlQ = useQuery({
    queryKey: ['video-url', id, completeVideo?.id],
    queryFn: () => api.get<{ url: string; content_type: string; session_offset_s: number | null }>(
      `/api/sessions/${id}/videos/${completeVideo!.id}/url`),
    enabled: !!completeVideo,
    staleTime: 5 * 60 * 1000,
  })

  const applyPendingSeek = () => {
    const v = videoRef.current
    if (v && pendingSeek !== null) {
      v.currentTime = pendingSeek
      v.play().catch(() => {})
      setPendingSeek(null)
    }
  }
  // Clicking a lap switches to the video tab and seeks once it can play.
  const jumpToLap = (offset: number) => { setTab('media'); setPendingSeek(offset) }
  useEffect(() => { if (tab === 'media') applyPendingSeek() },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, pendingSeek, videoUrlQ.data])

  // Copies the last recorded setup onto this session. The server does the copy,
  // so this is one request and cannot half-apply.
  const sameAsLast = useMutation({
    mutationFn: () => api.post(`/api/sessions/${id}/setup/same-as-last`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', id] }),
  })

  const setClass = useMutation({
    mutationFn: (race_class: string | null) =>
      api.put(`/api/sessions/${id}/class`, { race_class }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session', id] })
      // Track records are scoped by class, so they change too.
      qc.invalidateQueries({ queryKey: ['tracks'] })
    },
  })

  if (q.isLoading) return <div className="page"><Spinner /></div>
  if (q.isError || !q.data) {
    return <div className="page"><Empty title="Session not found"
      body="It may have been removed, or the link is wrong." /></div>
  }

  const s = q.data
  const timed = s.laps.filter((l) => l.is_valid && l.duration_s > 0)
  const best = timed.length ? Math.min(...timed.map((l) => l.duration_s)) : null
  const video = s.videos.find((v) => v.complete)

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{layoutName(s.track, s.layout)} — Run {s.run_id}</h1>
          <div className="sub">
            {dateTime(s.started_at, s.timezone)} · {s.n_laps} laps · {s.racer}
          </div>
        </div>
        <div className="row wrap">
          {/* Class is set here as well as on the setup sheet. The restrictor
              comes in and out during a practice day, and two runs in the other
              class should not need a 59-field form before their lap times stop
              polluting the record. */}
          <select value={s.race_class ?? ''} style={{ width: 'auto' }}
            onChange={(e) => setClass.mutate(e.target.value || null)}>
            <option value="">Class not set</option>
            <option value="Cadet 9">Cadet 9</option>
            <option value="Cadet 12">Cadet 12</option>
          </select>
          <SetupBadge status={s.setup_status} />
          <Link className="btn" to={`/sessions/${s.id}/setup`}>
            <SlidersHorizontal size={16} />
            {s.setup_status === 'none' ? 'Record setup' : 'Edit setup'}
          </Link>
          {/* The unchanged session is the one that gets skipped at a track,
              and it cannot be filled in afterwards - so it gets one tap. */}
          {s.setup_status === 'none' && (
            <button className="btn ghost" disabled={sameAsLast.isPending}
              onClick={() => sameAsLast.mutate()}>
              <Copy size={16} />
              {sameAsLast.isPending ? 'Recording…' : 'Nothing changed'}
            </button>
          )}
        </div>
      </div>

      {sameAsLast.isError && (
        <div className="advice warn" style={{ marginBottom: 14 }}>
          {(sameAsLast.error as Error).message}
        </div>
      )}

      <div className="grid metrics" style={{ marginBottom: 14 }}>
        <div className="card metric">
          <div className="label">Best lap</div>
          <div className="value lap best">{lapTime(best)}</div>
        </div>
        <div className="card metric">
          <div className="label">Median lap</div>
          <div className="value lap">{lapTime(s.median_lap_s)}</div>
        </div>
        <div className="card metric">
          <div className="label">Timed laps</div>
          <div className="value">{timed.length}</div>
        </div>
        <div className="card metric">
          <div className="label">Telemetry</div>
          <div className="value" style={{ fontSize: 20 }}>
            {s.telemetry_complete ? 'Complete' : 'Uploading'}
          </div>
          <div className="foot">{s.telemetry_complete ? 'All channels received' : 'Agent still syncing'}</div>
        </div>
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 14 }}>
        {(['laps', 'setup', 'media'] as const)
          .filter((t) => t !== 'setup' || s.setup_status !== 'hidden')
          .map((t) => (
          <button key={t}
            className={clsx('btn', tab === t ? '' : 'ghost', 'small')}
            onClick={() => setTab(t)}>
            {t === 'laps' ? 'Laps' : t === 'setup' ? 'Setup' : 'Video & photos'}
          </button>
        ))}
      </div>

      {tab === 'laps' && (
        <div className="card scroll-x">
          <table>
            <thead>
              <tr>
                <th>Lap</th>
                <th className="num">Time</th>
                <th className="num">Δ to best</th>
                <th>Video</th>
              </tr>
            </thead>
            <tbody>
              {s.laps.map((l) => {
                const isBest = best !== null && Math.abs(l.duration_s - best) < 1e-9
                return (
                  <tr key={l.number}>
                    <td>
                      {l.label || l.number}
                      {!l.is_valid && <span className="badge none" style={{ marginLeft: 8 }}>out/in</span>}
                    </td>
                    <td className={clsx('num lap', isBest && 'best')}>
                      {lapTime(l.duration_s)}
                    </td>
                    <td className="num dim">
                      {l.is_valid && best !== null && !isBest
                        ? `+${(l.duration_s - best).toFixed(3)}` : ''}
                    </td>
                    <td>
                      {l.video_offset_s !== null ? (
                        completeVideo ? (
                          <button className="btn ghost small" style={{ gap: 6 }}
                            onClick={() => jumpToLap(l.video_offset_s!)}>
                            <Play size={13} /> {clock(l.video_offset_s)}
                          </button>
                        ) : (
                          <span className="row small" style={{ gap: 6 }}>
                            <Play size={13} /> {clock(l.video_offset_s)}
                          </span>
                        )
                      ) : <span className="dim small">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'setup' && (
        <div className="card">
          {s.setup ? (
            <table>
              <tbody>
                {Object.entries(s.setup.values)
                  .filter(([, v]) => String(v ?? '').trim())
                  .map(([k, v]) => (
                    <tr key={k}><td className="muted">{k}</td><td>{v}</td></tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <Empty title="No setup recorded for this session"
              body="Record what was on the kart and the app works out what changed from last time."
              action={<Link className="btn" to={`/sessions/${s.id}/setup`}>Record setup</Link>} />
          )}
        </div>
      )}

      {tab === 'media' && (
        <div className="card">
          {video ? (
            <div>
              {videoUrlQ.isLoading && <Spinner />}
              {videoUrlQ.isError && (
                <Empty title="Couldn't load the video"
                  body="The playback link may have expired. Reopen the session to try again." />
              )}
              {videoUrlQ.data && (
                <>
                  <video
                    ref={videoRef}
                    src={videoUrlQ.data.url}
                    controls
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={applyPendingSeek}
                    onCanPlay={applyPendingSeek}
                    style={{
                      width: '100%', maxHeight: '70vh', borderRadius: 12,
                      background: '#000', display: 'block',
                    }}
                  />
                  <div className="row wrap small muted" style={{ marginTop: 10, gap: 8 }}>
                    <Camera size={14} />
                    SmartyCam footage
                    {video.session_offset_s !== null &&
                      ` — camera started ${video.session_offset_s.toFixed(0)}s before the session`}.
                  </div>
                  <div className="row wrap" style={{ gap: 6, marginTop: 10 }}>
                    {s.laps.filter((l) => l.video_offset_s !== null).map((l) => (
                      <button key={l.number} className="btn ghost small" style={{ gap: 6 }}
                        onClick={() => jumpToLap(l.video_offset_s!)}>
                        <Play size={12} /> {l.label || `Lap ${l.number}`}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <Empty title="No video for this session"
              body="SmartyCam footage is uploaded by the trackside agent after the session data, so it may still be on its way."
              action={<span className="row dim small" style={{ justifyContent: 'center' }}>
                <Camera size={15} /> Plug photos can be added here too
              </span>} />
          )}
        </div>
      )}
    </div>
  )
}

